import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { CalendarIcon, CloseIcon, FormsIcon } from "../../../../Icons";
import useConfirm from "../../../../hooks/useConfirm";
import InputDatePicker from "../../../EvaluationNew/components/InputDatePicker";
import { displayDateUS } from "../../../../utils/dateUtils";
import { PATIENT_THEME } from "../../patientTheme";
import {
  DISCIPLINES,
  getVisitTypeColor,
  formatVisitTypeLabel,
  filterStaffByDiscipline,
  getStaffEmployeeId,
  getRolesForDiscipline,
  getAssignableFormGroups,
  resolveVisitTypeFromFormType,
  defaultAssignedFormForStaff,
} from "./scheduleConstants";

const inputCls =
  "w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-colors";
const labelCls = "block text-xs font-medium text-slate-600 mb-1";
const stepBadgeCls =
  "inline-flex h-4 w-4 items-center justify-center rounded-full bg-indigo-100 text-[9px] font-bold text-indigo-700 mr-1.5";

const staffDisplayName = (s) =>
  s.name || `${s.firstName || ""} ${s.lastName || ""}`.trim() || "Staff";

const StepLabel = ({ step, children, required = false }) => (
  <label className={labelCls}>
    <span className={stepBadgeCls}>{step}</span>
    {children}
    {required ? " *" : ""}
  </label>
);

/**
 * Three questions only — Position, Staff, Visit Type. Time, status and notes
 * are left to the backend defaults; they are not part of the booking decision.
 *
 * Booking a new visit writes nothing here: the answers are handed to the form,
 * and the visit is created alongside it when the clinician saves.
 */
const ScheduleVisitModal = ({
  open,
  mode = "add",
  visit = null,
  defaultDate = "",
  assignedStaff = [],
  onClose,
  onSubmit,
  onDelete,
  saving = false,
  deleting = false,
}) => {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    visitDate: "",
    discipline: "RN",
    formType: "",
    staffId: "",
  });
  const { confirm, confirmDialog } = useConfirm();

  /** A filled visit locks the form. Unfilled assigned forms can be swapped. */
  const formFilled = Boolean(isEdit && visit?.formFilled);
  const canChangeForm = Boolean(isEdit && visit?.canChangeForm);
  const lockedForm =
    isEdit && visit && formFilled
      ? visit
      : isEdit && visit?.clinicalFormName && !canChangeForm
        ? visit
        : null;

  useEffect(() => {
    if (!open) return;
    if (isEdit && visit) {
      setForm({
        visitDate: visit.visitDate || "",
        discipline: visit.discipline || "RN",
        formType: visit.assignedFormType || "",
        staffId: visit.staffId ? String(visit.staffId) : "",
      });
    } else {
      setForm({
        visitDate: defaultDate || "",
        discipline: "RN",
        formType: defaultAssignedFormForStaff("RN", null).formType,
        staffId: "",
      });
    }
  }, [open, isEdit, visit, defaultDate]);

  const disciplineStaff = useMemo(
    () => filterStaffByDiscipline(assignedStaff, form.discipline),
    [assignedStaff, form.discipline],
  );

  const roleHint = useMemo(
    () => getRolesForDiscipline(form.discipline).join(" / "),
    [form.discipline],
  );

  const selectedStaff = useMemo(
    () =>
      disciplineStaff.find(
        (s) => String(getStaffEmployeeId(s)) === String(form.staffId),
      ),
    [disciplineStaff, form.staffId],
  );

  const formGroups = useMemo(
    () => getAssignableFormGroups(form.discipline, selectedStaff?.role),
    [form.discipline, selectedStaff?.role],
  );

  // Drop staff selection when it no longer matches the chosen discipline.
  useEffect(() => {
    if (!form.staffId) return;
    const stillValid = disciplineStaff.some(
      (s) => String(getStaffEmployeeId(s)) === String(form.staffId),
    );
    if (!stillValid) {
      setForm((prev) => ({ ...prev, staffId: "" }));
    }
  }, [form.staffId, disciplineStaff]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!open || formFilled) return;
    const allowed = formGroups.flatMap((g) => g.forms.map((f) => f.formType));
    const next = defaultAssignedFormForStaff(
      form.discipline,
      selectedStaff?.role,
    ).formType;
    setForm((prev) => {
      if (allowed.includes(prev.formType)) return prev;
      if (prev.formType === next) return prev;
      return { ...prev, formType: next };
    });
  }, [open, form.discipline, selectedStaff?.role, formFilled, formGroups]);

  const onDisciplineChange = (value) => {
    // Staff and form list are both scoped to the position, so both reset.
    setForm((prev) => ({ ...prev, discipline: value, staffId: "", formType: "" }));
  };

  const visitType = form.formType
    ? resolveVisitTypeFromFormType(form.formType)
    : isEdit
      ? visit?.visitType || null
      : null;
  const visitColor = getVisitTypeColor(visitType || "ROUTINE");

  const handleDeleteClick = async () => {
    if (!visit?.id || !onDelete || formFilled) return;
    const ok = await confirm({
      title: "Delete scheduled visit?",
      message: visit?.clinicalFormName
        ? `This removes the visit and its unfilled "${visit.clinicalFormName}" assignment.`
        : "This visit and its assigned (unfilled) form will be removed.",
    });
    if (!ok) return;
    onDelete(visit.id);
  };

  const submitPayload = (goToForm) => {
    const hasForm = Boolean(
      form.formType || visit?.clinicalFormId || visit?.dailyLogId,
    );
    if (formFilled) {
      if (!form.visitDate) {
        toast.error("Visit date is required");
        return;
      }
      onSubmit?.(
        { visitDate: form.visitDate },
        { goToForm: Boolean(goToForm && hasForm) },
      );
      return;
    }
    if (!form.visitDate) {
      toast.error("Visit date is required");
      return;
    }
    if (!form.discipline) {
      toast.error("Position is required");
      return;
    }
    if (!form.staffId) {
      toast.error("Staff is required");
      return;
    }
    if (!form.formType) {
      toast.error("Visit type is required");
      return;
    }
    onSubmit?.(
      {
        visitDate: form.visitDate,
        discipline: form.discipline,
        staffId: Number(form.staffId),
        formType: form.formType,
      },
      { goToForm: Boolean(goToForm && form.formType) },
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitPayload(false);
  };

  if (!open) return null;

  const identityLocked = formFilled;
  const canSave = formFilled
    ? Boolean(form.visitDate)
    : Boolean(form.visitDate && form.discipline && form.staffId && form.formType);
  const busy = saving || deleting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40">
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-xl"
      >
        <div
          className={`flex items-center justify-between gap-3 px-5 py-3 border-b ${PATIENT_THEME.modalHead}`}
        >
          <h3 className={`text-sm font-semibold ${PATIENT_THEME.modalTitle}`}>
            {isEdit ? "Edit Scheduled Visit" : "Schedule a New Visit"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 shrink-0"
            aria-label="Close"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4" noValidate>
          {isEdit ? (
            <div>
              <label className={labelCls}>Visit Date *</label>
              <InputDatePicker
                name="visitDate"
                formatSeparator="/"
                allowManualEntry
                compact
                value={form.visitDate}
                onChange={(e) => set("visitDate", e.target.value)}
                inputClassName={inputCls}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <CalendarIcon size={13} className="text-slate-400 shrink-0" />
              Visit date *
              <span className="font-semibold text-slate-800">
                {displayDateUS(form.visitDate) || "—"}
              </span>
            </div>
          )}

          <div>
            <StepLabel step={1} required>
              Position
            </StepLabel>
            <select
              required
              value={form.discipline}
              onChange={(e) => onDisciplineChange(e.target.value)}
              disabled={Boolean(lockedForm) || identityLocked}
              className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-500`}
            >
              {DISCIPLINES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <StepLabel step={2} required>
              Staff
            </StepLabel>
            <select
              required={!identityLocked}
              value={form.staffId}
              onChange={(e) => set("staffId", e.target.value)}
              disabled={identityLocked}
              className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-500`}
            >
              <option value="">— Select staff —</option>
              {disciplineStaff.map((s) => {
                const empId = getStaffEmployeeId(s);
                return (
                  <option key={`${s.role}-${empId}`} value={String(empId)}>
                    {staffDisplayName(s)}
                    {s.role ? ` (${s.role})` : ""}
                  </option>
                );
              })}
            </select>
            {identityLocked ? null : (
            <p className="mt-1 text-[10px] text-slate-400">
              {disciplineStaff.length
                ? `Showing ${roleHint} staff assigned to this patient`
                : `No ${roleHint || form.discipline} staff assigned to this patient — assign staff first`}
            </p>
            )}
          </div>

          <div>
            <StepLabel step={3} required>
              Visit Type
            </StepLabel>
            {lockedForm ? (
              <div className="flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-3">
                <FormsIcon size={12} className="text-indigo-500 shrink-0" />
                <span className="truncate text-sm text-slate-700">
                  {lockedForm.clinicalFormName || "Assigned form"}
                </span>
              </div>
            ) : (
              <>
                <select
                  required
                  value={form.formType}
                  onChange={(e) => set("formType", e.target.value)}
                  className={inputCls}
                >
                  <option value="">— Select a form —</option>
                  {formGroups.map((group) => (
                    <optgroup key={group.section} label={group.label}>
                      {group.forms.map((f) => (
                        <option key={f.formType} value={f.formType}>
                          {f.formName}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {isEdit && canChangeForm && !formFilled && (
                  <p className="mt-1 text-[10px] text-slate-400">
                    Form is assigned but not filled — you can change it.
                  </p>
                )}
                {isEdit && !form.formType && (
                  <p className="mt-1 text-[10px] text-slate-400">
                    This visit has no form yet — pick one to assign it.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex items-start gap-2 rounded-md border border-indigo-100 bg-indigo-50/50 px-3 py-2">
            <span
              className="mt-0.5 inline-block w-4 h-4 rounded-sm shrink-0 border border-white shadow-sm"
              style={{ backgroundColor: visitColor }}
            />
            <span className="text-xs text-slate-600">
              {visitType ? (
                <>
                  Calendar color:{" "}
                  <span className="font-semibold text-indigo-800">
                    {formatVisitTypeLabel(visitType)}
                  </span>
                  {form.formType && !isEdit && (
                    <>
                      {" "}
                      — Save assigns the empty form on the calendar. Open the
                      form later to fill it.
                    </>
                  )}
                  {formFilled && (
                    <>
                      {" "}
                      — Documentation is complete. The form type is locked.
                    </>
                  )}
                </>
              ) : (
                "Pick a visit type — the visit and its form are created together."
              )}
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <div>
              {isEdit && onDelete && visit?.canDelete !== false && !formFilled ? (
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  disabled={busy}
                  className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Delete Visit"}
                </button>
              ) : null}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || !canSave}
                className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                disabled={busy || !canSave || !form.formType}
                onClick={() => submitPayload(true)}
                className="inline-flex items-center justify-center gap-1.5 h-9 px-4 text-xs font-semibold text-white bg-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                <FormsIcon size={12} />
                Go to Form
              </button>
            </div>
          </div>
        </form>
      </div>
      {/* Outside the panel: the backdrop has no click handler, so the stacked
          confirm cannot bubble a dismiss into this modal. */}
      {confirmDialog}
    </div>
  );
};

export default ScheduleVisitModal;
